import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

import MainLayout from '../components/Layout/MainLayout';
import { useProjectContext } from '../contexts/ProjectContext';
import { formatDate } from '../utils/date';
import styles from '../assets/styles/ActionHistory.module.scss';
import { ReactComponent as AibexSymbol } from '../assets/icons/aibex-side-logo.svg';
import { ReactComponent as TrashIcon } from '../assets/icons/icon-trash.svg';

type HistoryStatus = 'verifying' | 'done';
type HistoryExecutor = 'AI' | 'HUMAN';

type HistoryListItem = {
  id: number;
  executed_at: string | null;
  executor_type: HistoryExecutor;
  execution_status: string;
  is_unread: boolean;
  platform: string;
  form_type: string;
  title: string;
  summary: string | null;
  target: {
    media: string | null;
    campaign_name: string | null;
    ad_group_name: string | null;
    ad_name?: string | null;
  };
  watch_metrics: {
    metrics: HistoryMetricCard[];
  };
};

type HistoryMetricCard = {
  key: string;
  label: string;
  before: number | null;
  current: number | null;
  unit: string | null;
  lower_is_better: boolean;
};

type HistoryComment = {
  comment_date: string | null;
  comment_type: string;
  comment_text: string;
  confidence_score: number | null;
};

type HistoryBeforeAfterCard = {
  campaign_name: string;
  before: Record<string, unknown>;
  after: Record<string, unknown>;
};

type HistoryMetricDefinition = {
  key: string;
  label: string;
  unit: string | null;
};

type HistoryVerificationPlan = {
  objective: string | null;
  primary_metric: HistoryMetricDefinition;
  success_metrics: HistoryMetricDefinition[];
  guardrail_metrics: HistoryMetricDefinition[];
  primary_granularity: string | null;
  comparison_dimension: string | null;
  evaluation_window_days: number | null;
  comment_focus: string[];
};

type HistoryDetail = {
  id: number;
  title: string;
  form_type: string;
  executed_at: string | null;
  executor_type: HistoryExecutor;
  execution_status: string;
  summary: string | null;
  target: {
    platform: string;
    campaign_name: string | null;
    ad_group_name: string | null;
    ad_name?: string | null;
  };
  proposal_reason: Record<string, unknown> | null;
  before_after: {
    before: Record<string, unknown>;
    after: Record<string, unknown>;
  };
  before_after_cards: HistoryBeforeAfterCard[];
  verification_plan: HistoryVerificationPlan;
  metric_trend: {
    labels: string[];
    series: Array<{
      key: string;
      label: string;
      unit: string | null;
      values: Array<number | null>;
    }>;
  };
  daily_comments: HistoryComment[];
  final_comment: HistoryComment | null;
};

type HistoryItem = {
  id: string;
  projectId: number;
  date: string;
  executor: 'AI' | 'Manual';
  status: HistoryStatus;
  isUnread: boolean;
  target: {
    media: string;
    campaign: string;
  };
  title: string;
  metrics: HistoryMetricCard[];
  details: {
    summary: string;
    beforeAfterCards: Array<{
      campaignName: string;
      before: string;
      after: string;
    }>;
    aiComment: string;
    graphData: number[];
    graphUnit: string | null;
  };
};

const BEFORE_VALUE_KEYS = [
  'before_status',
  'before_budget',
  'before_strategy',
  'before_schedules',
  'before_locations',
  'headlines',
  'descriptions',
  'devices',
  'groups',
  'keyword_text',
];

const AFTER_VALUE_KEYS = [
  'suggested_status',
  'suggested_budget',
  'suggested_strategy',
  'suggested_strategy_type',
  'suggested_strategy_value',
  'suggested_schedules',
  'suggested_locations',
  'device_bid_modifiers',
  'maintain',
  'headlines',
  'descriptions',
  'groups',
];

const OMITTED_DETAIL_KEYS = [
  'campaign_name',
  'ad_group_name',
  'ad_name',
  'reason',
  'applied_payload',
  'applied_result',
  'suggested_status',
  'suggested_budget',
  'suggested_strategy',
];

const SimpleLineChart = ({ data, unit }: { data: number[]; unit: string | null }) => {
  if (data.length === 0) {
    return null;
  }

  const width = 500;
  const height = 150;
  const padding = 20;
  const max = Math.max(...data);
  const min = Math.min(...data) * 0.9;
  const range = max - min || 1;
  const yLabels = [max, (max + min) / 2, min];

  const points = data
    .map((v, i) => {
      const x = (i / Math.max(data.length - 1, 1)) * (width - padding * 2) + padding;
      const y = height - padding - ((v - min) / range) * (height - padding * 2);
      return `${x},${y}`;
    })
    .join(' ');

  return (
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} className={styles.chartSvg}>
      {yLabels.map((val, i) => {
        const y = height - padding - ((val - min) / range) * (height - padding * 2);
        return (
          <g key={i}>
            <line x1={padding} y1={y} x2={width - padding} y2={y} className={styles.chartGridLine} />
            <text x={padding - 5} y={y + 4} className={styles.chartLabel}>
              {formatMetricValue(val, unit || '')}
            </text>
          </g>
        );
      })}

      <motion.path
        initial={{ pathLength: 0 }}
        animate={{ pathLength: 1 }}
        transition={{ duration: 1 }}
        d={`M ${points}`}
        fill="none"
        stroke="#00f2ff"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {data.map((v, i) => {
        const x = (i / Math.max(data.length - 1, 1)) * (width - padding * 2) + padding;
        const y = height - padding - ((v - min) / range) * (height - padding * 2);
        return <circle key={i} cx={x} cy={y} r="3" fill="#000" stroke="#00f2ff" strokeWidth="2" />;
      })}
    </svg>
  );
};

const toHistoryStatus = (status: string): HistoryStatus => (status === 'DONE' ? 'done' : 'verifying');

const formatMetricValue = (value: number | null | undefined, unit: string) => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '-';
  }
  if (unit === 'JPY') {
    return `¥${Math.round(value).toLocaleString()}`;
  }
  if (unit === 'PERCENT') {
    return `${Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
  }
  if (unit === 'RATIO') {
    return `${Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })}x`;
  }
  if (unit === 'COUNT') {
    return Number(value).toLocaleString();
  }
  return Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 });
};

const stringifyValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (Array.isArray(value)) {
    const items = value.map((item) => stringifyValue(item)).filter((item) => item !== '-');
    return items.length > 0 ? items.join(' / ') : '-';
  }
  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .map(([key, val]) => `${key}: ${stringifyValue(val)}`)
      .filter((item) => !item.endsWith(': -'));
    return entries.length > 0 ? entries.join('\n') : '-';
  }
  return '-';
};

const pickDisplayValue = (value: Record<string, unknown> | null | undefined, keys: string[]) => {
  if (!value) {
    return null;
  }

  for (const key of keys) {
    if (value[key] !== null && value[key] !== undefined && value[key] !== '') {
      return stringifyValue(value[key]);
    }
  }

  return null;
};

const summarizeForDisplay = (
  value: Record<string, unknown> | null | undefined,
  omittedKeys: string[]
): string => {
  if (!value) {
    return '-';
  }

  const filteredEntries = Object.entries(value).filter(
    ([key, entryValue]) =>
      !omittedKeys.includes(key) &&
      entryValue !== null &&
      entryValue !== undefined &&
      entryValue !== ''
  );

  if (filteredEntries.length === 0) {
    return '-';
  }

  if (filteredEntries.length === 1) {
    return stringifyValue(filteredEntries[0][1]);
  }

  return filteredEntries.map(([key, entryValue]) => `${key}: ${stringifyValue(entryValue)}`).join('\n');
};

const formatBeforeAfterContent = (detail: HistoryDetail | null) => {
  if (!detail) {
    return {
      before: '-',
      after: '-',
    };
  }

  const before = detail.before_after.before;
  const after = detail.before_after.after;

  return {
    before:
      pickDisplayValue(before, BEFORE_VALUE_KEYS) ??
      summarizeForDisplay(before, ['campaign_name', 'ad_group_name', 'ad_name', 'reason', 'suggested_status', 'suggested_budget', 'suggested_strategy']),
    after:
      pickDisplayValue(after, AFTER_VALUE_KEYS) ??
      summarizeForDisplay(after, OMITTED_DETAIL_KEYS),
  };
};

const buildBeforeAfterCards = (detail: HistoryDetail | null) => {
  if (!detail) {
    return [];
  }

  if (detail.before_after_cards.length > 0) {
    return detail.before_after_cards.map((card) => {
      const formatted = formatBeforeAfterContent({
        ...detail,
        before_after: {
          before: card.before,
          after: card.after,
        },
      });
      return {
        campaignName: card.campaign_name || '-',
        before: formatted.before,
        after: formatted.after,
      };
    });
  }

  const fallback = formatBeforeAfterContent(detail);
  return [
    {
      campaignName: detail.target.campaign_name || detail.target.ad_group_name || '-',
      before: fallback.before,
      after: fallback.after,
    },
  ];
};

const buildAiComment = (detail: HistoryDetail) => {
  if (detail.final_comment?.comment_text) {
    return detail.final_comment.comment_text;
  }
  if (detail.daily_comments.length > 0) {
    return detail.daily_comments[detail.daily_comments.length - 1].comment_text;
  }
  return '分析データを収集中です。数日後にご確認ください。';
};

const buildProposalReasonText = (detail: HistoryDetail | null) => {
  const rawReason =
    detail?.proposal_reason?.reason && typeof detail.proposal_reason.reason === 'string'
      ? detail.proposal_reason.reason.trim()
      : '';

  if (!detail) {
    return rawReason;
  }

  const summary = (detail.summary || '').trim();
  const normalizedReason = rawReason.replace(/\s+/g, '');
  const normalizedSummary = summary.replace(/\s+/g, '');
  const isNearDuplicate =
    !!normalizedReason &&
    !!normalizedSummary &&
    (normalizedSummary.includes(normalizedReason) || normalizedReason.includes(normalizedSummary));

  if (!rawReason || isNearDuplicate) {
    switch (detail.form_type) {
      case 'campaign_status':
        return '成果や配信実績のないキャンペーンを有効のまま残すと、予算配分や運用判断のノイズになり、成果の出ている配信へ集中しづらくなるため見直しが必要です。';
      case 'budget':
        return '現行予算のままだと、成果の強い配信で機会損失が起きたり、逆に弱い配信へ費用が残ったりするため、予算配分の見直しが必要です。';
      case 'device_targeting':
        return '成果の弱いデバイスにも同じ条件で配信を続けると、費用が分散して全体効率が落ちやすいため、デバイス別の配信調整が必要です。';
      case 'bidding_strategy':
        return '現行の入札戦略のままだと、直近の配信実態に対して最適化の方向がずれ、獲得効率や配信量の改善余地を取りこぼすため見直しが必要です。';
      case 'keyword_targeting':
        return '成果につながりにくいキーワードを残したままだと、関連性の低い流入に費用が使われ、獲得効率が悪化しやすいため見直しが必要です。';
      case 'ad_text':
        return '訴求の弱い広告文を使い続けると、クリック率や遷移後の反応が伸びず、配信機会と獲得効率の両方を損ねるため改善が必要です。';
      case 'add_image_asset':
        return '画像バリエーションが不足したままだと、配信面ごとの最適な組み合わせを試せず、表示機会や学習機会を逃しやすいため追加が必要です。';
      case 'replace_image_asset':
        return '反応の弱い画像を使い続けると、クリック率や配信評価が伸びず、同じ予算でも成果改善の余地を取りこぼすため差し替えが必要です。';
      case 'ad_schedule':
        return '成果の弱い曜日や時間帯にも配信を続けると、効率の高い時間帯へ回せる費用が薄まり、全体の費用対効果が落ちるため見直しが必要です。';
      case 'location_targeting':
        return '成果の弱い地域にも配信を広げたままだと、獲得見込みの低い流入へ費用が流れ、地域ごとの投資効率が悪化しやすいため見直しが必要です。';
      case 'demographics':
        return '相性の弱い属性にも同じ配信を続けると、反応の良い層への集中ができず、無駄な配信コストが増えやすいため見直しが必要です。';
      case 'cross_platform':
        return '媒体間の予算配分が現状のままだと、成果の高い媒体へ十分に投資できず、全体最適の機会を逃しやすいため見直しが必要です。';
      default:
        return rawReason;
    }
  }

  return rawReason;
};

const buildSummaryText = (detail: HistoryDetail | null, fallbackSummary: string | null | undefined) => {
  const summary = detail?.summary || fallbackSummary || '-';
  const reason = buildProposalReasonText(detail);

  if (!reason) {
    return summary;
  }

  return `${reason}\n${summary}`;
};

const isLowerBetterMetric = (metricKey: string | null | undefined) =>
  ['cpa', 'cost', 'cpc', 'cpm'].includes(String(metricKey || '').toLowerCase());

const buildMetricCards = (item: HistoryListItem, detail: HistoryDetail | null): HistoryMetricCard[] => {
  if (detail) {
    return detail.metric_trend.series.map((series) => {
      const numericValues = series.values.filter((value): value is number => typeof value === 'number');
      return {
        key: series.key,
        label: series.label,
        before: numericValues[0] ?? null,
        current: numericValues.length > 0 ? numericValues[numericValues.length - 1] : null,
        unit: series.unit,
        lower_is_better: isLowerBetterMetric(series.key),
      };
    });
  }

  return item.watch_metrics.metrics.map((metric) => ({
    key: metric.key,
    label: metric.label,
    before: metric.before,
    current: metric.current,
    unit: metric.unit,
    lower_is_better: metric.lower_is_better,
  }));
};

const mapListItemToHistoryItem = (item: HistoryListItem, detail: HistoryDetail | null, projectId: number): HistoryItem => {
  const metricCards = buildMetricCards(item, detail);
  const graphData = (detail?.metric_trend.series[0]?.values ?? []).filter(
    (value): value is number => typeof value === 'number'
  );
  const beforeAfterCards = buildBeforeAfterCards(detail);

  return {
    id: String(item.id),
    projectId,
    date: formatDate(item.executed_at),
    executor: item.executor_type === 'AI' ? 'AI' : 'Manual',
    status: toHistoryStatus(item.execution_status),
    isUnread: item.is_unread,
    target: {
      media: item.target.media || item.platform,
      campaign: item.target.campaign_name || item.target.ad_group_name || '-',
    },
    title: item.title,
    metrics: metricCards,
    details: {
      summary: buildSummaryText(detail, item.summary),
      beforeAfterCards,
      aiComment: detail ? buildAiComment(detail) : '分析データを収集中です。数日後にご確認ください。',
      graphData,
      graphUnit: detail?.metric_trend.series[0]?.unit || item.watch_metrics.metrics[0]?.unit || null,
    },
  };
};

const ActionHistory: React.FC = () => {
  const { selectedProjectId } = useProjectContext();
  const [historyData, setHistoryData] = useState<HistoryItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<HistoryItem | null>(null);
  const [loading, setLoading] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [searchQuery, setSearchQuery] = useState('');
  const [filterExecutor, setFilterExecutor] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    const fetchHistory = async () => {
      if (!selectedProjectId) {
        setHistoryData([]);
        setSelectedItem(null);
        return;
      }

      setLoading(true);
      setErrorMessage('');
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/marketing-actions/list?project_id=${selectedProjectId}`,
          { credentials: 'include' }
        );
        if (!res.ok) {
          throw new Error('施策一覧の取得に失敗しました。');
        }
        const list = (await res.json()) as HistoryListItem[];
        const mapped = list.map((item) => mapListItemToHistoryItem(item, null, selectedProjectId));
        setHistoryData(mapped);
      } catch (error) {
        console.error('Fetch marketing actions failed:', error);
        setErrorMessage('施策一覧の取得に失敗しました。');
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [selectedProjectId]);

  const fetchDetail = async (item: HistoryItem) => {
    if (!selectedProjectId) return;

    setDrawerLoading(true);
    setSelectedItem(item);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/marketing-actions/detail?action_id=${item.id}&project_id=${selectedProjectId}`,
        { credentials: 'include' }
      );
      if (!res.ok) {
        throw new Error('施策詳細の取得に失敗しました。');
      }
      const detail = (await res.json()) as HistoryDetail;
      const nextItem = mapListItemToHistoryItem(
        {
          id: Number(item.id),
          executed_at: detail.executed_at,
          executor_type: detail.executor_type,
          execution_status: detail.execution_status,
          is_unread: item.isUnread,
          platform: detail.target.platform,
          form_type: '',
          title: detail.title,
          summary: detail.summary,
          target: {
            media: detail.target.platform,
            campaign_name: detail.target.campaign_name,
            ad_group_name: detail.target.ad_group_name,
            ad_name: detail.target.ad_name,
          },
          watch_metrics: {
            metrics: detail.metric_trend.series.map((series, index) => {
              const numericValues = series.values.filter((value): value is number => typeof value === 'number');
              const currentItemMetric = item.metrics[index];
              return {
                key: series.key,
                label: series.label,
                before: numericValues[0] ?? currentItemMetric?.before ?? null,
                current:
                  numericValues.length > 0
                    ? numericValues[numericValues.length - 1]
                    : currentItemMetric?.current ?? currentItemMetric?.before ?? null,
                unit: series.unit,
                lower_is_better: isLowerBetterMetric(series.key),
              };
            }),
          },
        },
        detail,
        selectedProjectId
      );
      setHistoryData((prev) => prev.map((historyItem) => (historyItem.id === item.id ? nextItem : historyItem)));
      setSelectedItem(nextItem);
    } catch (error) {
      console.error('Fetch marketing action detail failed:', error);
      setErrorMessage('施策詳細の取得に失敗しました。');
    } finally {
      setDrawerLoading(false);
    }
  };

  const handleMarkAsRead = async (id: string) => {
    if (!selectedProjectId) return;

    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/marketing-actions/read`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action_id: Number(id),
          project_id: selectedProjectId,
        }),
      });

      if (!res.ok) {
        throw new Error('施策の既読更新に失敗しました。');
      }

      setHistoryData((prev) =>
        prev.map((item) => (item.id === id ? { ...item, isUnread: false } : item))
      );
      setSelectedItem((prev) => (prev && prev.id === id ? { ...prev, isUnread: false } : prev));
    } catch (error) {
      console.error('Mark marketing action as read failed:', error);
      setErrorMessage('施策の既読更新に失敗しました。');
    }
  };

  const handleDeleteAction = async (id: string) => {
    if (!selectedProjectId) return;

    const shouldDelete = window.confirm('この施策と関連する日次監視データ・AIコメントを削除します。よろしいですか？');
    if (!shouldDelete) {
      return;
    }

    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/marketing-actions/delete`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action_id: Number(id),
          project_id: selectedProjectId,
        }),
      });

      if (!res.ok) {
        throw new Error('施策の削除に失敗しました。');
      }

      setHistoryData((prev) => prev.filter((item) => item.id !== id));
      setSelectedItem((prev) => (prev?.id === id ? null : prev));
    } catch (error) {
      console.error('Delete marketing action failed:', error);
      setErrorMessage('施策の削除に失敗しました。');
    }
  };

  const filteredData = useMemo(() => {
    return historyData.filter((item) => {
      const matchSearch =
        item.title.includes(searchQuery) || item.details.summary.includes(searchQuery);
      const matchExecutor =
        filterExecutor === 'all' ||
        (filterExecutor === 'ai' && item.executor === 'AI') ||
        (filterExecutor === 'manual' && item.executor === 'Manual');
      const matchStatus = filterStatus === 'all' || item.status === filterStatus;
      const matchUnread = !showUnreadOnly || item.isUnread;

      let matchDate = true;
      if (startDate) {
        matchDate = matchDate && item.date >= `${startDate.replace(/-/g, '/')} 00:00`;
      }
      if (endDate) {
        matchDate = matchDate && item.date.slice(0, 10) <= endDate.replace(/-/g, '/');
      }

      return matchSearch && matchExecutor && matchStatus && matchUnread && matchDate;
    });
  }, [endDate, filterExecutor, filterStatus, historyData, searchQuery, showUnreadOnly, startDate]);

  const getComparisonClass = (before: number | null | undefined, after: number | null | undefined, lowerIsBetter: boolean) => {
    if (before === null || before === undefined || after === null || after === undefined) {
      return styles.valueNeutral;
    }
    if (before === after) return styles.valueNeutral;
    const isBetter = lowerIsBetter ? after < before : after > before;
    return isBetter ? styles.valueBetter : styles.valueWorse;
  };

  return (
    <MainLayout title="施策履歴・自動検証">
      <div className={styles.header}>
        <div className={styles.filterBar}>
          <div className={styles.searchBox}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              type="text"
              placeholder="施策名から検索"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className={styles.selectGroup}>
            <select className={styles.select} value={filterExecutor} onChange={(e) => setFilterExecutor(e.target.value)}>
              <option value="all">実施者: すべて</option>
              <option value="ai">AI</option>
              <option value="manual">手動</option>
            </select>
            <select className={styles.select} value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="all">ステータス: すべて</option>
              <option value="verifying">検証中</option>
              <option value="done">終了</option>
            </select>
          </div>

          <div className={styles.dateFilterGroup}>
            <input
              type="date"
              className={`${styles.select} ${styles.dateInput}`}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
            <span>~</span>
            <input
              type="date"
              className={`${styles.select} ${styles.dateInput}`}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>

          <label className={styles.unreadToggle}>
            <input
              type="checkbox"
              checked={showUnreadOnly}
              onChange={(e) => setShowUnreadOnly(e.target.checked)}
            />
            未読のみを表示
          </label>
        </div>
      </div>

      {errorMessage && <div>{errorMessage}</div>}

      <div className={styles.tableContainer}>
        <table>
          <thead>
            <tr>
              <th className={styles.colType}>種別</th>
              <th className={styles.colStatus}>ステータス</th>
              <th className={styles.colDate}>実施日</th>
              <th className={styles.colTarget}>対象</th>
              <th>施策名</th>
              <th>ウォッチ指標(2週間)</th>
              <th className={styles.colAction} />
            </tr>
          </thead>
          <tbody>
            {!loading && filteredData.length === 0 ? (
              <tr>
                <td colSpan={7}>施策履歴はまだありません。</td>
              </tr>
            ) : (
              filteredData.map((item) => (
                <tr
                  key={item.id}
                  onClick={() => {
                    void fetchDetail(item);
                  }}
                  className={item.isUnread ? styles.unread : ''}
                >
                  <td align="center">
                    <div className={`${styles.executorIcon} ${item.executor === 'AI' ? styles.ai : ''}`}>
                      {item.executor === 'AI' ? (
                        <AibexSymbol className={`${styles.aibexMark} ${styles.white}`} />
                      ) : (
                        '人'
                      )}
                    </div>
                  </td>
                  <td>
                    <span className={`${styles.statusBadge} ${styles[item.status]}`}>
                      {item.status === 'verifying' ? '検証中' : '検証終了'}
                    </span>
                  </td>
                  <td>{item.date}</td>
                  <td>
                    <div className={styles.mediaInfo}>
                      <div className={styles.mediaIconContainer}>{item.target.media[0] || '-'}</div>
                      <div>
                        <div className={styles.mediaName}>{item.target.media}</div>
                        <div className={styles.campaignName}>{item.target.campaign}</div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className={styles.rowTitle}>{item.title}</span>
                    {item.isUnread && <span className={styles.unreadBadge}>未読</span>}
                  </td>
                  <td>
                    <div className={styles.metrics}>
                      {item.metrics.map((metric) => (
                        <div className={styles.metricItem} key={metric.key}>
                          <span className={styles.label}>{metric.label}</span>
                          <span className={styles.value}>
                            {item.status === 'done' && metric.current !== undefined ? (
                              <>
                                <span className={styles.valueOld}>{formatMetricValue(metric.before, metric.unit || '')}</span>
                                <span className={getComparisonClass(metric.before, metric.current, metric.lower_is_better)}>
                                  → {formatMetricValue(metric.current, metric.unit || '')}
                                </span>
                              </>
                            ) : (
                              formatMetricValue(metric.before, metric.unit || '')
                            )}
                          </span>
                        </div>
                      ))}
                    </div>
                  </td>
                  <td>
                    <button
                      type="button"
                      className={styles.actionDeleteButton}
                      aria-label="施策を削除"
                      onClick={(event) => {
                        event.stopPropagation();
                        void handleDeleteAction(item.id);
                      }}
                    >
                      <TrashIcon />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <AnimatePresence>
        {selectedItem && (
          <>
            <motion.div
              className={styles.drawerBackdrop}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedItem(null)}
            />
            <motion.div
              className={styles.drawer}
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            >
              <div className={styles.drawerHeader}>
                <div className={styles.drawerHeaderMain}>
                  <div className={styles.drawerDate}>{selectedItem.date}</div>
                  <h3 className={styles.drawerTitle}>{selectedItem.title}</h3>
                  <span className={`${styles.statusBadge} ${styles[selectedItem.status]}`}>
                    {selectedItem.status === 'verifying' ? '検証中' : '検証終了'}
                  </span>
                </div>
                <button className={styles.closeBtn} onClick={() => setSelectedItem(null)}>
                  ×
                </button>
              </div>

              <div className={styles.drawerContent}>
                <div className={styles.section}>
                  <h3>施策概要</h3>
                  <p className={styles.summaryText}>{selectedItem.details.summary}</p>
                </div>

                <div className={styles.section}>
                  <h3>設定変更 (Before / After)</h3>
                  <div className={styles.compareCardList}>
                    {selectedItem.details.beforeAfterCards.map((card, index) => (
                      <div className={styles.compareCard} key={`${card.campaignName}-${index}`}>
                        <div className={styles.compareCardCampaignLabel}>対象キャンペーン</div>
                        <div className={styles.compareCardCampaignName}>{card.campaignName}</div>
                        <div className={styles.compareBox}>
                          <div className={styles.compareItem}>
                            <h4>Before</h4>
                            <div className={styles.content}>{card.before}</div>
                          </div>
                          <div className={`${styles.compareItem} ${styles.after}`}>
                            <h4>After</h4>
                            <div className={styles.content}>{card.after}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className={styles.section}>
                  <h3>主要指標の推移</h3>
                  <div className={styles.metricsCompare}>
                    {selectedItem.metrics.map((metric) => (
                      <div className={styles.metric} key={metric.key}>
                        <div className={styles.label}>{metric.label}</div>
                        <div
                          className={`${styles.valGroup} ${getComparisonClass(metric.before, metric.current, metric.lower_is_better) === styles.valueWorse ? styles.worse : ''}`}
                        >
                          <span className={styles.prev}>{formatMetricValue(metric.before, metric.unit || '')}</span>
                          <span className={styles.arrow}>→</span>
                          <span className={styles.curr}>{formatMetricValue(metric.current ?? metric.before, metric.unit || '')}</span>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className={styles.chartContainer}>
                    {drawerLoading ? (
                      <div>詳細を読み込み中です。</div>
                    ) : (
                      <SimpleLineChart data={selectedItem.details.graphData} unit={selectedItem.details.graphUnit} />
                    )}
                  </div>
                </div>

                <div className={styles.section}>
                  <h3>AI分析コメント</h3>
                  <div className={styles.aiComment}>
                    <div className={styles.aiIconWrapper}>
                      <AibexSymbol className={`${styles.aibexMark} ${styles.large}`} />
                    </div>
                    <p>{selectedItem.details.aiComment}</p>
                  </div>
                </div>
              </div>

              <div className={styles.actionArea}>
                <button
                  className={styles.confirmBtn}
                  onClick={() => {
                    void handleMarkAsRead(selectedItem.id);
                  }}
                  disabled={!selectedItem.isUnread}
                >
                  {selectedItem.isUnread ? '内容を確認しました（未読解消）' : '確認済み'}
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </MainLayout>
  );
};

export default ActionHistory;
